import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { Icon, divIcon, latLngBounds, type Map as LeafletMap } from 'leaflet';
import Supercluster from 'supercluster';
import styled, { keyframes } from 'styled-components';
import { colors, spacing, typography, borderRadius, breakpoints } from '../../styles/theme';
import type { Event } from '../../types/api';
import config from '../../config';
import { getEventCoordinates } from '../../utils/eventFields';
import { isMappable } from '../../utils/eventPresentation';
import {
  DEFAULT_VIBE_STYLE,
  PROXIMITY_OPACITY,
  VIBE_STYLES,
  buildPinHtml,
  getMarkerOpacity,
  getVibeStyle,
} from '../../utils/eventMapStyles';
import MapSelectionPane from './MapSelectionPane';

const MapShell = styled.div`
  position: relative;
  width: 100%;
  height: 420px;
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${colors.border.light};

  .leaflet-container {
    height: 100%;
    width: 100%;
  }

  @media (max-width: ${breakpoints.md}) {
    height: 360px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  padding: ${spacing[4]};
  border: 1px dashed ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  background: ${colors.background.secondary};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  text-align: center;
`;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

const MapSkeleton = styled.div`
  width: 100%;
  height: 420px;
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.border.light};
  background: linear-gradient(
    90deg,
    ${colors.background.secondary} 25%,
    ${colors.background.tertiary} 50%,
    ${colors.background.secondary} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};

  @media (max-width: ${breakpoints.md}) {
    height: 360px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]} ${spacing[3]};
  margin-top: ${spacing[2]};
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const LegendSwatch = styled.span.withConfig({
  shouldForwardProp: prop => prop !== 'accent' && prop !== 'swatchOpacity',
})<{ accent: string; swatchOpacity: number }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.accent};
  opacity: ${props => props.swatchOpacity};
`;

delete (Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: config.leafletIcons.iconRetinaUrl,
  iconUrl: config.leafletIcons.iconUrl,
  shadowUrl: config.leafletIcons.shadowUrl,
});

interface MappedEvent {
  event: Event;
  lat: number;
  lng: number;
  source: 'venue' | 'location';
}

interface LocationFeatureProperties {
  locationKey: string;
  eventIds: number[];
}

type LocationFeature = Supercluster.PointFeature<LocationFeatureProperties>;
type MapCluster = Supercluster.ClusterFeature<Supercluster.AnyProps> | LocationFeature;

function locationKey(point: Pick<MappedEvent, 'lat' | 'lng'>): string {
  return `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;
}

function createEventPin(event: Event, isHighlighted: boolean) {
  const style = getVibeStyle(event);
  const size = isHighlighted ? 40 : 32;
  return divIcon({
    className: 'event-pin',
    html: buildPinHtml({
      color: style.color,
      opacity: getMarkerOpacity(event),
      isHighlighted,
    }),
    iconSize: [size, size * 1.25],
    iconAnchor: [size / 2, size * 1.25],
  });
}

function createClusterPin(count: number) {
  const size = count >= 100 ? 48 : count >= 10 ? 42 : 38;
  return divIcon({
    className: 'event-cluster-pin',
    html: `<span aria-hidden="true" style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:50%;background:${colors.primary[700]};border:3px solid white;color:white;font-weight:700;box-shadow:0 2px 8px rgba(0,0,0,.28)">${count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const FitToMarkers: React.FC<{ points: MappedEvent[] }> = ({ points }) => {
  const map = useMap();
  const signature = points.map(point => `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`).join('|');
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 12);
      return;
    }
    map.fitBounds(latLngBounds(points.map(point => [point.lat, point.lng] as [number, number])), {
      padding: [40, 40],
      maxZoom: 13,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, map]);
  useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(), 0);
    return () => window.clearTimeout(timer);
  }, [map]);
  return null;
};

const ViewportClusters: React.FC<{
  index: Supercluster<LocationFeatureProperties, Supercluster.AnyProps>;
  pointsByLocation: Map<string, MappedEvent[]>;
  eventsById: Map<number, Event>;
  highlightedEventId: number | null;
  selectedEventId: number | null;
  onSelectLocation: (events: Event[], eventId: number) => void;
  onClose: () => void;
}> = ({
  index,
  pointsByLocation,
  eventsById,
  highlightedEventId,
  selectedEventId,
  onSelectLocation,
  onClose,
}) => {
  const map = useMap();
  const [clusters, setClusters] = useState<MapCluster[]>([]);

  const refresh = useCallback(() => {
    const bounds = map.getBounds();
    setClusters(index.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      Math.round(map.getZoom()),
    ) as MapCluster[]);
  }, [index, map]);
  useMapEvents({
    moveend: refresh,
    zoomend: refresh,
    click: event => {
      if ((event.originalEvent.target as HTMLElement)?.closest?.('.leaflet-marker-icon')) return;
      onClose();
    },
  });
  useEffect(() => refresh(), [refresh]);

  return (
    <>
      {clusters.map(feature => {
        const [lng, lat] = feature.geometry.coordinates;
        const properties = feature.properties as Supercluster.ClusterProperties & LocationFeatureProperties;
        if (properties.cluster) {
          const clusterId = Number(properties.cluster_id);
          const count = Number(properties.point_count);
          return (
            <Marker
              key={`cluster-${clusterId}`}
              position={[lat, lng]}
              icon={createClusterPin(count)}
              keyboard
              title={`${count} event locations. Activate to zoom in.`}
              eventHandlers={{
                click: event => {
                  event.originalEvent.stopPropagation();
                  const zoom = Math.min(index.getClusterExpansionZoom(clusterId), map.getMaxZoom());
                  map.setView([lat, lng], zoom, { animate: true });
                },
              }}
            />
          );
        }

        const grouped = pointsByLocation.get(properties.locationKey) || [];
        const events = grouped.map(point => point.event);
        const activeId = selectedEventId != null && properties.eventIds.includes(selectedEventId)
          ? selectedEventId
          : properties.eventIds[0];
        const representative = eventsById.get(activeId) || events[0];
        if (!representative) return null;
        const highlighted = properties.eventIds.some(id => id === highlightedEventId || id === selectedEventId);
        return (
          <Marker
            key={`location-${properties.locationKey}`}
            position={[lat, lng]}
            icon={createEventPin(representative, highlighted)}
            zIndexOffset={highlighted ? 1000 : 0}
            keyboard
            title={events.length > 1 ? `${events.length} events at this location` : representative.title}
            eventHandlers={{
              click: event => {
                event.originalEvent.stopPropagation();
                onSelectLocation(events, activeId);
              },
            }}
          />
        );
      })}
    </>
  );
};

export interface EventLocationMapProps {
  events: Event[];
  highlightedEventId?: number | null;
  selectedEventId?: number | null;
  onMarkerSelect?: (eventId: number) => void;
  onSelectionClose?: () => void;
  onFindInList?: (eventId: number) => void;
  onViewEvent?: (eventId: number) => void;
  emptyMessage?: string;
  isLoading?: boolean;
}

export const EventLocationMap: React.FC<EventLocationMapProps> = ({
  events,
  highlightedEventId = null,
  selectedEventId = null,
  onMarkerSelect,
  onSelectionClose,
  onFindInList,
  onViewEvent,
  emptyMessage = 'None of these events have a mapped location yet. They are all listed below.',
  isLoading = false,
}) => {
  const mapRef = useRef<LeafletMap | null>(null);
  const [selectedLocationEvents, setSelectedLocationEvents] = useState<Event[]>([]);

  const points = useMemo<MappedEvent[]>(() => (events || []).flatMap(event => {
    if (!isMappable(event)) return [];
    const coordinates = getEventCoordinates(event);
    if (!coordinates) return [];
    return [{
      event,
      lat: coordinates.latitude,
      lng: coordinates.longitude,
      source: coordinates.source,
    }];
  }), [events]);

  const pointsByLocation = useMemo(() => {
    const grouped = new Map<string, MappedEvent[]>();
    points.forEach(point => {
      const key = locationKey(point);
      grouped.set(key, [...(grouped.get(key) || []), point]);
    });
    return grouped;
  }, [points]);
  const eventsById = useMemo(() => new Map(events.map(event => [event.eventId, event])), [events]);
  const clusterIndex = useMemo(() => {
    const features: LocationFeature[] = [];
    pointsByLocation.forEach((grouped, key) => {
      features.push({
        type: 'Feature',
        properties: { locationKey: key, eventIds: grouped.map(point => point.event.eventId) },
        geometry: { type: 'Point', coordinates: [grouped[0].lng, grouped[0].lat] },
      });
    });
    return new Supercluster<LocationFeatureProperties, Supercluster.AnyProps>({
      radius: 55,
      maxZoom: 16,
      minPoints: 2,
    }).load(features);
  }, [pointsByLocation]);

  useEffect(() => {
    if (selectedEventId == null) {
      setSelectedLocationEvents([]);
      return;
    }
    const selectedPoint = points.find(point => point.event.eventId === selectedEventId);
    if (!selectedPoint) {
      setSelectedLocationEvents([]);
      onSelectionClose?.();
      return;
    }
    const grouped = pointsByLocation.get(locationKey(selectedPoint)) || [selectedPoint];
    setSelectedLocationEvents(grouped.map(point => point.event));
    mapRef.current?.panTo([selectedPoint.lat, selectedPoint.lng], { animate: true });
  }, [selectedEventId, points, pointsByLocation, onSelectionClose]);

  const visibleStyles = useMemo(() => {
    const present = new Set(points.map(point => getVibeStyle(point.event).key));
    const styles = VIBE_STYLES.filter(style => present.has(style.key));
    if (present.has(DEFAULT_VIBE_STYLE.key)) styles.push(DEFAULT_VIBE_STYLE);
    return styles;
  }, [points]);

  if (isLoading && points.length === 0) {
    return <MapSkeleton role="status" aria-live="polite" aria-label="Loading map">Loading map...</MapSkeleton>;
  }
  if (points.length === 0) return <EmptyState role="status">{emptyMessage}</EmptyState>;

  return (
    <>
      <MapShell>
        <MapContainer
          center={[points[0].lat, points[0].lng]}
          zoom={10}
          scrollWheelZoom={false}
          ref={mapRef}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer attribution={config.externalServices.leafletTileAttribution} url={config.externalServices.leafletTileUrl} />
          <FitToMarkers points={points} />
          <ViewportClusters
            index={clusterIndex}
            pointsByLocation={pointsByLocation}
            eventsById={eventsById}
            highlightedEventId={highlightedEventId}
            selectedEventId={selectedEventId}
            onSelectLocation={(locationEvents, eventId) => {
              setSelectedLocationEvents(locationEvents);
              onMarkerSelect?.(eventId);
            }}
            onClose={() => onSelectionClose?.()}
          />
        </MapContainer>
        {selectedEventId != null && selectedLocationEvents.length > 0 && (
          <MapSelectionPane
            events={selectedLocationEvents}
            activeEventId={selectedEventId}
            onSelectEvent={eventId => onMarkerSelect?.(eventId)}
            onClose={() => onSelectionClose?.()}
            onFindInList={eventId => onFindInList?.(eventId)}
            onViewEvent={onViewEvent}
          />
        )}
      </MapShell>
      <Legend aria-label="Map legend">
        {visibleStyles.map(style => (
          <LegendItem key={style.key}>
            <LegendSwatch accent={style.color} swatchOpacity={1} />
            {style.label}
          </LegendItem>
        ))}
        <LegendItem>
          <LegendSwatch accent={colors.gray[700]} swatchOpacity={PROXIMITY_OPACITY.month} />
          Faded = further out
        </LegendItem>
      </Legend>
    </>
  );
};

export default EventLocationMap;
